import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Scale, MapPin } from "lucide-react";
import { EASE } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1534237710431-e2fc698436d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NxM3NTV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBza3lzY3JhcGVyJTIwYnVzaW5lc3N8ZW58MHx8fHwxNzg5NjY5OTQ0fDA&ixlib=rb-4.1.0&q=85";

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-2 -mb-2">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.15, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20">
      <motion.div style={{ opacity: glowOpacity }} className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/4 w-[700px] h-[700px] rounded-full bg-emerald-500/15 blur-[160px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-emerald-400/10 blur-[140px]" />
        <div
          className="absolute inset-0 opacity-[0.13]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(52,211,153,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(52,211,153,0.35) 1px, transparent 1px)",
            backgroundSize: "88px 88px",
            maskImage: "radial-gradient(ellipse 90% 70% at 50% 30%, black, transparent)",
          }}
        />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center w-full">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
            className="inline-flex items-center gap-2.5 rounded-full border border-emerald-400/25 bg-emerald-400/5 px-4 py-2 mb-8"
            data-testid="hero-urgency-pill"
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-beacon" />
            <span className="font-jbmono text-[11px] uppercase tracking-[0.25em] text-emerald-300">
              2027 — Seulement 7 agences ouvriront
            </span>
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.45}>Deviens manager</MaskedLine>
            <MaskedLine delay={0.58}>indépendant.</MaskedLine>
            <MaskedLine delay={0.71}>
              <span className="text-emerald-400 drop-shadow-[0_0_32px_rgba(52,211,153,0.35)]">
                Monte ton agence.
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
            className="mt-8 max-w-xl text-base md:text-lg text-slate-400 leading-relaxed"
          >
            Alternativ recrute ses futurs ATM — managers indépendants en énergie &amp; services —
            partout en France. Reçois le dossier complet et découvre si ta place t&rsquo;attend.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="https://candidature.alternativ-france.fr/dossier"
              data-testid="hero-cta-dossier"
              className="group inline-flex items-center gap-2 rounded-full bg-emerald-400 text-[#070C0A] px-7 py-4 text-sm font-bold transition-all duration-300 hover:bg-emerald-300 hover:shadow-[0_0_36px_rgba(52,211,153,0.5)]"
            >
              Recevoir mon dossier
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#simulation"
              data-testid="hero-cta-simulation"
              className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 px-7 py-4 text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-emerald-400/60 hover:text-emerald-300"
            >
              Simuler mes revenus
              <ArrowDown className="w-4 h-4" />
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.9, ease: EASE }}
          className="relative hidden lg:block"
        >
          <motion.div style={{ y: imgY }} className="relative">
            <div className="absolute -inset-6 bg-emerald-400/15 blur-3xl rounded-full" />
            <div className="relative rounded-[2rem] overflow-hidden border border-emerald-400/20 shadow-[0_40px_120px_-30px_rgba(16,185,129,0.35)]">
              <img
                src={HERO_IMG}
                alt="Architecture d'affaires moderne"
                data-testid="hero-image"
                className="w-full h-[540px] object-cover scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070C0A] via-transparent to-transparent" />
            </div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1.5, ease: EASE }}
              className="absolute -left-10 top-16 rounded-2xl border border-emerald-400/20 bg-[#0E1713]/90 backdrop-blur-md px-5 py-4"
            >
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-emerald-400/15 grid place-items-center text-emerald-300">
                  <Scale className="w-4 h-4" />
                </span>
                <div>
                  <p className="font-jbmono text-emerald-300 font-bold">Juridique</p>
                  <p className="text-[11px] text-slate-400">accompagnement juridique inclus</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1.7, ease: EASE }}
              className="absolute -right-6 bottom-20 rounded-2xl border border-emerald-400/20 bg-[#0E1713]/90 backdrop-blur-md px-5 py-4"
            >
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-emerald-400/15 grid place-items-center text-emerald-300">
                  <MapPin className="w-4 h-4" />
                </span>
                <div>
                  <p className="font-jbmono text-emerald-300 font-bold">7</p>
                  <p className="text-[11px] text-slate-400">territoires exclusifs en France</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#vision"
        aria-label="Défiler"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-emerald-400/60 hover:text-emerald-300 transition-colors"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }} className="block">
          <ArrowDown className="w-5 h-5" />
        </motion.span>
      </motion.a>
    </section>
  );
}
